import { i18nPrice } from '@shared/fondof-fn/format/price'
import type { Promotion } from '@shared/shop-api/cart/types'
import { getCollection } from '@shared/shop-api/category/api'
import { toCategory } from '@shared/shop-api/category/mapper'
import { StorefrontError } from '@shared/shop-api/core/error'
import { isClientData } from '@shared/shop-api/core/helper'
import { execute } from '@shared/shop-api/core/storefront'
import {
  toFilterInput,
  toProductFilter,
} from '@shared/shop-api/filter/product/mapper'
import type { ProductFilterInput } from '@shared/shop-api/filter/product/types'
import { toInfoPage } from '@shared/shop-api/page/mapper'
import { toCursorPagination } from '@shared/shop-api/pagination/mapper'
import {
  isPromotedProductDiscountRuleAmount,
  isPromotedProductDiscountRulePercentage,
} from '@shared/shop-api/product/attributes/check'
import { toProduct } from '@shared/shop-api/product/mapper'
import { toProductWithVariants } from '@shared/shop-api/product/mapper-with-variants'
import {
  backwardSearchQuery,
  forwardSearchQuery,
  productByIdQuery,
  productQuery,
  productsByCategoryQuery,
  productsByMetafield,
  productsQuery,
  searchSuggestionsQuery,
} from '@shared/shop-api/product/query'
import type { CatalogSearchSort } from '@shared/shop-api/product/types'
import type {
  LanguageCode,
  ProductCollectionSortKeys,
  ProductWithVariantsFragment,
  SearchSortKeys,
} from '@shared/types/storefront/graphql'

export const getProduct = async (handle: string) => {
  const productData = await execute(productQuery, {
    handle,
  })

  if (!isClientData(productData)) {
    throw new StorefrontError('Invalid product data response', productData)
  }

  return toProductWithVariants(productData.product)
}

export const getProductById = async (id: string) => {
  const productData = await execute(productByIdQuery, {
    id: `gid://shopify/Product/${id}`,
  })

  if (!isClientData(productData)) {
    throw new StorefrontError('Invalid product data response', productData)
  }

  return toProductWithVariants(productData.product)
}

type ProductVariable = {
  query: string
  language?: LanguageCode
  filter?: ProductFilterInput[]
  sortKey?: SearchSortKeys
  ipp?: number
}

export const getProducts = async (
  ids: (string | number)[],
  productFilters?: ProductFilterInput[],
  sortKey?: SearchSortKeys,
  ipp?: number,
  language?: LanguageCode
) => {
  if (ids.length === 0) {
    return []
  }

  let variables: ProductVariable = {
    query: ids
      .map((id) => (isNaN(Number(id)) ? `handle:${id}` : `id:${id}`))
      .join(' OR '),
    filter: productFilters,
    sortKey,
    ipp,
  }

  if (language) {
    variables = { ...variables, language }
  }

  const productsData = await execute(productsQuery, variables)

  if (!isClientData(productsData)) {
    throw new StorefrontError('Invalid products data response', productsData)
  }

  return productsData.search.items.map((item) =>
    toProductWithVariants(item as ProductWithVariantsFragment)
  )
}

const getProductsByFilter = async (
  filter: ProductFilterInput[],
  ipp?: number,
  query?: string
) => {
  const productsData = await execute(productsByMetafield, {
    filter,
    first: ipp,
    query,
  })

  if (!isClientData(productsData)) {
    throw new StorefrontError('Invalid product data response', productsData)
  }

  return productsData.search.items.map((item) =>
    toProductWithVariants(item as ProductWithVariantsFragment)
  )
}

export const getSortedProducts = async (filter: string[] = [], ipp: number) => {
  const collection = await getCollection(
    'product-grid-all-products-sorted',
    filter,
    'forward',
    ipp
  )

  return collection.collection.products
}

export const getProductsByModel = async (model: string, ipp = 250) => {
  return await getSortedProducts(['filter.p.m.pim.model=' + model], ipp)
}

export const getProductsByStyle = async (style: string) => {
  return await getProductsByFilter([
    { productMetafield: { namespace: 'pim', key: 'style', value: style } },
  ])
}

export const getProductsBySubCategoryAndSize = async (
  subCategory: string,
  size: string,
  ipp = 250
) => {
  return await getSortedProducts(
    [
      'filter.p.m.pim.sub_category=' + subCategory,
      'filter.v.option.size=' + size,
    ],
    ipp
  )
}

export const getProductByStyleAndModel = async (
  model: string,
  style: string
) => {
  return await getProductsByFilter([
    { productMetafield: { namespace: 'pim', key: 'model', value: model } },
    { productMetafield: { namespace: 'pim', key: 'style', value: style } },
  ])
}

export const getProductsByStyleAndSize = async (
  style: string,
  size: string
) => {
  return await getProductsByFilter([
    { variantOption: { name: 'size', value: size } },
    { productMetafield: { namespace: 'pim', key: 'style', value: style } },
  ])
}

type ProductsByCategory = {
  categoryId: number
  ipp: number
  sortKey?: ProductCollectionSortKeys
  productFilter?: ProductFilterInput[]
}

export const getProductsByCategory = async ({
  categoryId,
  ipp,
  sortKey,
  productFilter,
}: ProductsByCategory) => {
  const productsData = await execute(productsByCategoryQuery, {
    categoryId: `gid://shopify/Collection/${categoryId}`,
    first: ipp,
    sortKey,
    filter: productFilter,
  })

  if (!isClientData(productsData)) {
    throw new StorefrontError('Invalid products data response', productsData)
  }

  return {
    ...productsData.collection,
    products: productsData.collection.products.items.map(toProductWithVariants),
  }
}

export const getCatalogSearchSuggestions = async (query: string) => {
  const searchSuggestionsData = await execute(searchSuggestionsQuery, {
    pageQuery: `${query} title:search`,
    productQuery: query,
    collectionQuery: query,
  })

  if (!isClientData(searchSuggestionsData)) {
    throw new StorefrontError(
      'Invalid products data response',
      searchSuggestionsData
    )
  }

  const products = searchSuggestionsData.productResults.products.map(toProduct)

  const infos = searchSuggestionsData.pageResults.pages.map(toInfoPage)

  return {
    completions: products.map((product) => product.name),
    content: infos.map((info) => info.title),
    infos,
    products,
    categories:
      searchSuggestionsData.collectionResults.collections.map(toCategory),
  }
}

type CatalogSearchProps = {
  query: string
  filter: string[]
  ipp: number
  sort: CatalogSearchSort | undefined
  cursor?: string | undefined
  direction?: string | undefined
}

export const getCatalogSearch = async ({
  query,
  filter,
  ipp,
  sort,
  cursor,
  direction,
}: CatalogSearchProps) => {
  const mappedFilter = filter
    .map(toFilterInput)
    .filter((item) => item !== undefined)

  const requestQuery =
    direction === 'backward' ? backwardSearchQuery : forwardSearchQuery

  const searchData = await execute(requestQuery, {
    query,
    filter: mappedFilter,
    ipp,
    desc: sort?.startsWith('-'),
    sort: sort?.replace('-', '') as SearchSortKeys | undefined,
    cursor,
  })

  if (!isClientData(searchData)) {
    throw new StorefrontError('Invalid search data response', searchData)
  }

  return {
    products: searchData.search.items.map((item) =>
      toProductWithVariants(item as ProductWithVariantsFragment)
    ),
    cursorPagination: toCursorPagination({
      ...searchData.search.pageInfo,
      totalCount: searchData.search.totalCount,
    }),
    filters: searchData.search.productFilters.map((filter) =>
      toProductFilter(
        filter,
        searchData.baseFilterSearch.productFilters.find(
          (baseFilter) => baseFilter.id === filter.id
        )
      )
    ),
  }
}

export const getPromotionWithProduct = async (promotion: Promotion) => {
  const products = await getProducts(promotion.items)

  return {
    ...promotion,
    products: products.map((product) => {
      const variants = product.variants.map((variant) => {
        let { price } = variant

        if (
          isPromotedProductDiscountRulePercentage(promotion?.discountRule.type)
        ) {
          price =
            variant.price -
            variant.price * promotion?.discountRule.type.percentage
        }

        if (isPromotedProductDiscountRuleAmount(promotion?.discountRule.type)) {
          price = variant.price - promotion?.discountRule.type.amount
        }

        const discount =
          variant.price === 0
            ? 100
            : Math.round(100 - (price * 100) / variant.price)

        return {
          ...variant,
          originalPrice: variant.price,
          originalPriceFormatted: variant.priceFormatted,
          price,
          priceFormatted: i18nPrice(price, variant.currencyCode),
          discount,
          isOffer: price < variant.originalPrice,
        }
      })

      const minVariantPrice = variants.reduce((minPriceVariant, variant) => {
        if (!minPriceVariant || variant.price < minPriceVariant.price) {
          return variant
        }

        return minPriceVariant
      }, variants[0])

      const hasVariantWithOffer = variants.some((variant) => variant.isOffer)
      const offeredVariants = variants.filter((variant) => variant.isOffer)

      const maxOriginalPriceFormatted = variants.reduce((max, variant) =>
        variant.originalPrice > max.originalPrice ? variant : max
      ).originalPriceFormatted

      return {
        ...product,
        hasVariantWithOffer,
        maxDiscount: hasVariantWithOffer
          ? Math.max(...offeredVariants.map((variant) => variant.discount))
          : 0,
        maxOriginalPriceFormatted,
        priceRange: {
          ...product.priceRange,
          minPriceFormatted:
            minVariantPrice?.priceFormatted ??
            product.priceRange.minPriceFormatted,
        },
        variants,
      }
    }),
  }
}
